import { Component, DestroyRef, inject, OnInit, OnDestroy, Inject, Injector, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CryptoPriceData, PriceChartDuration, PriceDataPoint } from 'src/app/models/price-chart';
import { CryptoPriceService } from 'src/app/services/crypto-price.service';
import { CurrenciesService } from 'src/app/api/currencies.service';
import { MetadataService } from 'src/app/api/metadata.service';
import { TickerCard } from 'src/app/models/market-ticker.model';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'market-ticker',
  templateUrl: './market-ticker.component.html',
  styleUrls: ['./market-ticker.component.scss'],
  standalone: false,
})
export class MarketTickerComponent implements OnInit, OnDestroy {
  cards: TickerCard[] = [];
  loading = true;
  readonly skeletonCards = Array.from({ length: 8 });
  colorUp = '#00F098';
  readonly colorDown = '#FF5F56';

  private destroyRef = inject(DestroyRef);
  private priceServices: CryptoPriceService[] = [];
  private readonly TICKER_COUNT = 8;

  constructor(
    private currenciesService: CurrenciesService,
    private metadataService: MetadataService,
    private injector: Injector,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    const resolved = getComputedStyle(document.documentElement).getPropertyValue('--accent-neon-green').trim();
    if (resolved) this.colorUp = resolved;

    this.metadataService.userCurrency().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      currency => this.initTicker(currency),
      () => this.initTicker(environment.currencies.defaultCurrency)
    );
  }

  private initTicker(baseCurrency: string) {
    this.currenciesService.getCryptoWithSupportedBase(baseCurrency).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(currencies => {
      const topCurrencies = currencies.slice(0, this.TICKER_COUNT);
      this.cards = topCurrencies.map((c, i) => ({
        pair: `${c.code}/${baseCurrency}`,
        code: c.code,
        baseCurrency,
        price: null,
        changePercent: null,
        isUp: true,
        sparklinePath: '',
        sparklineFill: '',
        gradientId: `ticker-grad-${i}`,
      }));

      topCurrencies.forEach((currency, index) => {
        const childInjector = Injector.create({
          providers: [{ provide: CryptoPriceService }],
          parent: this.injector,
        });
        const service = childInjector.get(CryptoPriceService);
        this.priceServices.push(service);

        service.setBaseCurrency(baseCurrency);
        service.setCurrency(currency.code);
        service.setDuration(PriceChartDuration.SevenDays);

        service.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
          if (data?.baseCurrency?.lastPrice != null) {
            this.loading = false;
            this.updateCard(index, data);
          }
        });
      });
    });
  }

  private updateCard(index: number, data: CryptoPriceData) {
    if (!this.cards[index]) return;

    const card = this.cards[index];
    card.price = data.baseCurrency?.lastPrice;
    card.changePercent = data.priceChangePercentage;
    card.isUp = data.isPositvitePriceChange;

    const chartData = data.priceHistory?.baseCurrencyData?.data;
    if (chartData?.length > 1) {
      const chronological = [...chartData].reverse();
      const sparkline = this.buildSparkline(chronological, 100, 30);
      card.sparklinePath = sparkline.linePath;
      card.sparklineFill = sparkline.fillPath;
    }
  }

  private buildSparkline(data: PriceDataPoint[], width: number, height: number) {
    const prices = data.map(p => p.y);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const range = max - min || 1;
    const step = width / (prices.length - 1);

    const points = prices.map((p, i) => ({
      x: (i * step).toFixed(1),
      y: (height - ((p - min) / range) * height).toFixed(1),
    }));

    const linePath = points.map((pt, i) => (i === 0 ? `M${pt.x},${pt.y}` : `L${pt.x},${pt.y}`)).join(' ');
    const fillPath = `${linePath} L${width},${height} L0,${height} Z`;
    return { linePath, fillPath };
  }

  ngOnDestroy() {
    this.priceServices.forEach(s => {
      if (s.ngOnDestroy) s.ngOnDestroy();
    });
  }
}
